import React from "react";
import { Stack, Option } from "@mui/joy";
import { Box, FormControl, FormLabel } from "@mui/joy";
import DarkInput from "../../../common/DarkInput";
import DarkSelect from "../../../common/DarkSelect";
import DarkTypography from "../../../common/DarkTypography";
import CommonModal from "../../../common/CommonModal";
import type {
  CreateTrainingTemplatePayload,
  ExternalOptionItem,
} from "../../../../api/training/Training.typed";
import type { QualificationItem } from "../../../../api/qualification/Qualification.typed";
import useAppSettings from "../../../../contexts/app/appSettings";
import { useQualification } from "../../../../contexts/api";

interface CreateTrainingTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: CreateTrainingTemplatePayload) => Promise<void> | void;
  credits: ExternalOptionItem[];
  qualifications: ExternalOptionItem[];
  isSubmitting?: boolean;
}

export default function CreateTrainingTemplateModal(props: CreateTrainingTemplateModalProps) {
  const { open, onClose, onCreate, credits, qualifications, isSubmitting } = props;
  const { config } = useAppSettings();
  const isSync = !!config?.CustomersSync;
  const { listQualificationItems } = useQualification();

  const [agency, setAgency] = React.useState<string>("");
  const [qualificationName, setQualificationName] = React.useState<string>("");
  const [zCreditID, setZCreditID] = React.useState<number | null>(null);
  const [zTrainingId, setZTrainingId] = React.useState<number | null>(null);
  const [zQualificationId, setZQualificationId] = React.useState<number | null>(null);
  const [destinationQualificationLocalId, setDestinationQualificationLocalId] = React.useState<
    number | null
  >(null);
  const [localCatalogQualifications, setLocalCatalogQualifications] = React.useState<
    QualificationItem[]
  >([]);

  const sortedCredits = React.useMemo(() => {
    return [...credits].sort((a, b) => a.Name.localeCompare(b.Name));
  }, [credits]);
  const sortedQualifications = React.useMemo(() => {
    return [...qualifications].sort((a, b) => a.Name.localeCompare(b.Name));
  }, [qualifications]);

  React.useEffect(() => {
    if (!open) {
      setAgency("");
      setQualificationName("");
      setZCreditID(null);
      setZTrainingId(null);
      setZQualificationId(null);
      setDestinationQualificationLocalId(null);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open || isSync) {
      setLocalCatalogQualifications([]);
      return;
    }
    void listQualificationItems().then((rows) => setLocalCatalogQualifications(rows ?? []));
  }, [open, isSync, listQualificationItems]);

  const sortedLocalCatalogQualifications = React.useMemo(
    () =>
      [...localCatalogQualifications].sort((itemA, itemB) =>
        (itemA.sigle || "").localeCompare(itemB.sigle || "", undefined, { sensitivity: "base" }),
      ),
    [localCatalogQualifications],
  );

  const handleSubmit = React.useCallback(async () => {
    if (!agency.trim() || !qualificationName.trim()) return;
    const payload: CreateTrainingTemplatePayload = {
      Agency: agency.trim(),
      Qualification: qualificationName.trim(),
      IsSDI: false,
      NeedsTheory: false,
      NeedsRifap: false,
      Cards: { Card: [] },
    };
    if (isSync) {
      if (zCreditID == null || zTrainingId == null || zQualificationId == null) return;
      payload.ZCreditID = zCreditID;
      payload.ZTrainingId = zTrainingId;
      payload.ZQualificationId = zQualificationId;
    } else if (destinationQualificationLocalId != null && destinationQualificationLocalId > 0) {
      payload.DestinationQualificationID = destinationQualificationLocalId;
    }
    await onCreate(payload);
  }, [
    agency,
    qualificationName,
    isSync,
    zCreditID,
    zTrainingId,
    zQualificationId,
    destinationQualificationLocalId,
    onCreate,
  ]);

  const handleModalClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <CommonModal
      open={open}
      onClose={handleModalClose}
      title="Nouveau template"
      size="md"
      onConfirm={handleSubmit}
      confirmLabel="Créer"
      submitDisabled={!!isSubmitting || !agency.trim() || !qualificationName.trim()}
    >
      <Box sx={{ minWidth: 520 }}>
        <Stack spacing={2}>
          <FormControl>
            <FormLabel>Organisation</FormLabel>
            <DarkInput value={agency} onChange={(e) => setAgency(e.target.value)} fullWidth />
          </FormControl>
          <FormControl>
            <FormLabel>Nom de formation</FormLabel>
            <DarkInput
              value={qualificationName}
              onChange={(e) => setQualificationName(e.target.value)}
              fullWidth
            />
          </FormControl>
          {!isSync && (
            <FormControl>
              <FormLabel>Qualification enregistrée chez le client après validation</FormLabel>
              <DarkSelect
                placeholder="Non définie"
                value={destinationQualificationLocalId ?? 0}
                onChange={(_e, selected) => {
                  setDestinationQualificationLocalId(
                    typeof selected === "number" && selected > 0 ? selected : null,
                  );
                }}
              >
                <Option value={0}>Non définie</Option>
                {sortedLocalCatalogQualifications.map((qu) => (
                  <Option key={qu.id} value={qu.id}>
                    {qu.sigle}
                    {qu.name ? ` · ${qu.name}` : ""}
                  </Option>
                ))}
              </DarkSelect>
            </FormControl>
          )}
          {isSync && (
            <>
              <FormControl>
                <FormLabel>ID externe du produit</FormLabel>
                <DarkSelect
                  value={zCreditID}
                  onChange={(e, v) => setZCreditID(typeof v === "string" ? Number(v) : v)}
                >
                  {sortedCredits.map((c) => (
                    <Option key={c.ID} value={c.ID}>
                      {c.ID} - {c.Name}
                    </Option>
                  ))}
                </DarkSelect>
              </FormControl>
              <FormControl>
                <FormLabel>ID externe de la qualification de la formation</FormLabel>
                <DarkSelect
                  value={zTrainingId}
                  onChange={(e, v) => setZTrainingId(typeof v === "string" ? Number(v) : v)}
                >
                  {sortedQualifications.map((q) => (
                    <Option key={q.ID} value={q.ID}>
                      {q.ID} - {q.Name}
                    </Option>
                  ))}
                </DarkSelect>
              </FormControl>
              <FormControl>
                <FormLabel>ID externe de la qualification finale</FormLabel>
                <DarkSelect
                  value={zQualificationId}
                  onChange={(e, v) => setZQualificationId(typeof v === "string" ? Number(v) : v)}
                >
                  {sortedQualifications.map((q) => (
                    <Option key={q.ID} value={q.ID}>
                      {q.ID} - {q.Name}
                    </Option>
                  ))}
                </DarkSelect>
              </FormControl>
            </>
          )}
          <DarkTypography level="body-xs" colorVariant="muted">
            Les champs obligatoires doivent etre renseignes pour creer le template.
          </DarkTypography>
        </Stack>
      </Box>
    </CommonModal>
  );
}
